import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Autocomplete, TextField, CircularProgress, Grid } from "@mui/material";
import { Control, useController } from "react-hook-form";
import { gridSizeMdLg6, stableStringify } from "@/shared";

/**
 * Props del componente CustomAutocompleteFetchForm.
 * Muestra un autocomplete que carga opciones desde un endpoint paginado.
 */
export interface CustomAutocompleteFetchProps<
  T,
  _TFormValues = any,
  V extends string | number | null = string | number | null,
  P = unknown
> {
  name: string;
  control: Control<any, any>;

  label?: string;
  placeholder?: string;
  disabled?: boolean;

  fetchData: (
    searchValue: string,
    page: number,
    isAll: boolean,
    keySearch: keyof T,
    params?: any
  ) => Promise<{
    options: T[];
    hasMore: boolean;
  }>;

  loadOptionByValue?: (value: Exclude<V, null>) => Promise<T | null>;

  getOptionLabel: (option: T) => string;
  getOptionKey: (option: T) => string | number;

  initialInputValue?: string;

  onChangeValue?: (value: V | null) => void;
  onChangeRawValue?: (value: T | null) => void;

  size?: { xs: number; sm: number; md: number; lg: number };
  required?: boolean;

  fetchParams?: P;
  keySearch?: keyof T;
  isAll?: boolean;
}

export default function CustomAutocompleteFetchForm<
  T,
  TFormValues = any,
  V extends string | number | null = string | number | null,
  P = unknown
>(props: CustomAutocompleteFetchProps<T, TFormValues, V, P>) {
  const {
    name: nameProps,
    control,
    label,
    placeholder,
    disabled,
    fetchData,
    loadOptionByValue,
    getOptionLabel,
    getOptionKey,
    initialInputValue = "",
    onChangeValue,
    onChangeRawValue,
    size = gridSizeMdLg6,
    required = true,
    fetchParams,
    keySearch = "nombre" as keyof T,
    isAll = false,
  } = props;

  // --------- RHF: useController ----------
  const {
    field: { value, onChange, onBlur, ref },
    fieldState: { error },
  } = useController<any, typeof nameProps>({
    name: nameProps,
    control,
  });

  // ========= Refs para “blindar” props inestables =========
  const fetchDataRef = useRef(fetchData);
  const fetchParamsRef = useRef(fetchParams);
  const getOptionKeyRef = useRef(getOptionKey);
  const getOptionLabelRef = useRef(getOptionLabel);
  const loadOptionByValueRef = useRef(loadOptionByValue);
  const onChangeValueRef = useRef(onChangeValue);
  const onChangeRawValueRef = useRef(onChangeRawValue);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);
  useEffect(() => {
    fetchParamsRef.current = fetchParams;
  }, [fetchParams]);
  useEffect(() => {
    getOptionKeyRef.current = getOptionKey;
  }, [getOptionKey]);
  useEffect(() => {
    getOptionLabelRef.current = getOptionLabel;
  }, [getOptionLabel]);
  useEffect(() => {
    loadOptionByValueRef.current = loadOptionByValue;
  }, [loadOptionByValue]);
  useEffect(() => {
    onChangeValueRef.current = onChangeValue;
  }, [onChangeValue]);
  useEffect(() => {
    onChangeRawValueRef.current = onChangeRawValue;
  }, [onChangeRawValue]);

  // --------- Estados internos ----------
  const [search, setSearch] = useState<string>(initialInputValue);
  const [options, setOptions] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isResetting, setIsResetting] = useState(false);

  const searchTrimmed = search.trim();
  const isAllEffective = isAll || searchTrimmed === "";

  // Helper: unique por key, usando ref (no depende de identidad externa)
  const uniqByKey = useCallback((arr: T[]) => {
    const map = new Map<string | number, T>();
    for (const item of arr) {
      map.set(getOptionKeyRef.current(item), item);
    }
    return Array.from(map.values());
  }, []);

  const paramsSignature = useMemo(
    () => stableStringify(fetchParams ?? null),
    [fetchParams]
  );

  // --------- Fetch inicial + por búsqueda (debounce) ----------
  useEffect(() => {
    // Capturar los params actuales para este efecto
    const currentParams = fetchParams;
    const currentFetchData = fetchData;

    // Resetear estado inmediatamente antes del fetch
    setIsResetting(true);
    setOptions([]);
    setPage(1);
    setHasMore(true);

    let isCancelled = false;
    const t = window.setTimeout(async () => {
      setLoading(true);

      try {
        const { options: newOptions, hasMore: more } =
          await currentFetchData(
            searchTrimmed,
            1,
            isAllEffective,
            keySearch,
            currentParams
          );

        // Solo actualizar si no fue cancelado
        if (!isCancelled) {
          setOptions(newOptions);
          setPage(1);
          setHasMore(more);
        }
      } catch (err) {
        if (!isCancelled) {
          setOptions([]);
          setHasMore(false);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
          setIsResetting(false);
        }
      }
    }, 400);

    return () => {
      isCancelled = true;
      window.clearTimeout(t);
    };
  }, [searchTrimmed, isAllEffective, keySearch, paramsSignature, fetchParams, fetchData]);

  // --------- Carga paginada (scroll infinito) ----------
  const loadMoreOptions = useCallback(async () => {
    // No cargar más si está reseteando, cargando, o no hay más páginas
    if (isResetting || loading || !hasMore) return;

    setLoading(true);
    try {
      const next = page + 1;

      const { options: moreOpts, hasMore: more } = await fetchData(
        searchTrimmed,
        next,
        isAllEffective,
        keySearch,
        fetchParams
      );

      setOptions((prev) => uniqByKey([...prev, ...moreOpts]));
      setPage(next);
      setHasMore(more);
    } catch {
      // Si falla (ej: 404), marcar que no hay más páginas
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [isResetting, loading, hasMore, page, searchTrimmed, isAllEffective, keySearch, uniqByKey, fetchData, fetchParams]);

  // --------- Opción seleccionada a partir del value de RHF ----------
  const selectedOption: T | null = useMemo(() => {
    // Tratar string vacío como null
    if (value == null || value === "") return null;

    return (
      options.find(
        (opt) => String(getOptionKeyRef.current(opt)) === String(value)
      ) ?? null
    );
  }, [options, value]);

  // --------- Precargar informacion ----------
  useEffect(() => {
    let mounted = true;

    const loader = loadOptionByValueRef.current;
    const needLoad =
      value != null && value !== "" && !selectedOption && typeof loader === "function" && !isResetting;

    if (!needLoad) return;

    (async () => {
      try {
        const opt = await loader!(value as Exclude<V, null>);
        if (!mounted || !opt) return;

        // Inserta la opción precargada en el pool
        setOptions((prev) => uniqByKey([opt, ...prev]));
      } catch {
        // Silenciar errores de precarga
      }
    })();

    return () => {
      mounted = false;
    };
  }, [value, selectedOption, uniqByKey, isResetting, paramsSignature]);

  // ------------- Limpiar ---------------------
  // (Ya no es necesario - el reseteo se hace en el efecto de búsqueda)

  // --------- Handlers del Autocomplete ----------
  const handleChange = useCallback(
    (_: React.SyntheticEvent, option: T | null) => {
      const newValue = (
        option ? (getOptionKeyRef.current(option) as V) : null
      ) as V;

      onChange(newValue);
      onChangeValueRef.current?.(newValue);
      onChangeRawValueRef.current?.(option);
    },
    [onChange]
  );

  // Solo cambiar search cuando el usuario escribe (no al seleccionar / reset)
  const handleInputChange = useCallback(
    (
      _: React.SyntheticEvent,
      v: string,
      reason: "input" | "reset" | "clear"
    ) => {
      if (reason === "clear") setSearch("");
      if (reason === "input") setSearch(v);
      // reason === "reset" -> no tocar search (evita fetch por selección)
    },
    []
  );

  // Normalizar el valor para que null y "" sean tratados igual
  const normalizedValue = selectedOption;

  return (
    <Grid item {...size}>
      <Autocomplete
        popupIcon={null}
        disabled={disabled}
        options={options}
        loading={loading}
        value={normalizedValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getOptionLabel={(opt) => {
          if (!opt) return '';
          return getOptionLabelRef.current(opt);
        }}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          return (
            getOptionKeyRef.current(option) ===
            getOptionKeyRef.current(val as T)
          );
        }}
        loadingText="Cargando..."
        noOptionsText="Sin resultados"
        ListboxProps={{
          onScroll: (e: React.UIEvent<HTMLElement>) => {
            const n = e.currentTarget;
            if (n.scrollTop + n.clientHeight >= n.scrollHeight - 1) {
              loadMoreOptions();
            }
          },
          style: { maxHeight: 320, overflow: "auto" },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            required={required}
            variant="outlined"
            inputRef={ref}
            label={label}
            placeholder={placeholder}
            onBlur={onBlur}
            error={!!error}
            helperText={error?.message}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Grid>
  );
}
